import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class FAQAnimation {
  private section: HTMLElement | null = null;
  private faqItems: HTMLElement[] = [];
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private openIndex: number | null = null;
  private timelines: Map<number, gsap.core.Timeline> = new Map();

  constructor() {
    this.section = document.querySelector('.faq-section');
    if (!this.section) {
      console.warn('⚠️ FAQ section not found');
      return;
    }

    this.title = this.section.querySelector('.faq-title') as HTMLElement;
    this.subtitle = this.section.querySelector('.faq-subtitle') as HTMLElement;
    this.faqItems = Array.from(this.section.querySelectorAll('.faq-item')) as HTMLElement[];

    this.init();
  }

  private init(): void {
    this.setInitialStates();
    this.setupScrollAnimations();
    this.setupAccordionInteractions();
  }

  private setInitialStates(): void {
    if (this.title) {
      gsap.set(this.title, {
        opacity: 0,
        y: -50,
        scale: 0.9,
      });
    }

    if (this.subtitle) {
      gsap.set(this.subtitle, {
        opacity: 0,
        y: 30,
      });
    }

    this.faqItems.forEach((item) => {
      const question = item.querySelector('.faq-question') as HTMLElement;
      const answer = item.querySelector('.faq-answer') as HTMLElement;
      const answerContent = item.querySelector('.faq-answer-content') as HTMLElement;

      gsap.set(item, {
        opacity: 0,
        y: 60,
        scale: 0.95,
      });

      if (question) {
        gsap.set(question, {
          rotationX: -90,
          transformOrigin: 'top center',
        });
      }

      if (answer) {
        gsap.set(answer, {
          height: 0,
          opacity: 1,
        });
      }

      if (answerContent) {
        gsap.set(answerContent, {
          opacity: 0,
          y: -10,
        });
      }
    });
  }

  private setupScrollAnimations(): void {
    if (!this.section) return;

    if (this.title) {
      ScrollTrigger.create({
        trigger: this.section,
        start: 'top 80%',
        onEnter: () => {
          const tl = gsap.timeline();
          tl.to(this.title, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: 'elastic.out(1, 0.5)',
          });
        },
      });
    }

    if (this.subtitle) {
      ScrollTrigger.create({
        trigger: this.section,
        start: 'top 75%',
        onEnter: () => {
          gsap.to(this.subtitle, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            delay: 0.2,
          });
        },
      });
    }

    this.faqItems.forEach((item) => {
      ScrollTrigger.create({
        trigger: item,
        start: 'top 85%',
        onEnter: () => {
          const tl = gsap.timeline();

          tl.to(item, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            ease: 'power3.out',
          }, 0);

          const question = item.querySelector('.faq-question') as HTMLElement;
          if (question) {
            tl.to(question, {
              rotationX: 0,
              duration: 0.8,
              ease: 'power3.out',
            }, 0.1);
          }

          const icon = item.querySelector('.faq-icon') as HTMLElement;
          if (icon) {
            tl.from(icon, {
              rotation: -180,
              scale: 0,
              duration: 0.6,
              ease: 'back.out(1.7)',
            }, 0.3);
          }
        },
      });
    });
  }

  private setupAccordionInteractions(): void {
    this.faqItems.forEach((item, index) => {
      const question = item.querySelector('.faq-question') as HTMLElement;
      const answer = item.querySelector('.faq-answer') as HTMLElement;
      const answerContent = item.querySelector('.faq-answer-content') as HTMLElement;

      if (!question || !answer || !answerContent) return;

      question.addEventListener('click', () => {
        const isOpen = this.openIndex === index;

        this.faqItems.forEach((_, otherIndex) => {
          if (otherIndex !== index) {
            this.closeItem(otherIndex);
          }
        });

        if (isOpen) {
          this.closeItem(index);
        } else {
          this.openItem(index);
        }
      });
    });
  }

  private openItem(_index: number): void {
    const item = this.faqItems[_index];
    if (!item) return;

    const question = item.querySelector('.faq-question') as HTMLElement;
    const answer = item.querySelector('.faq-answer') as HTMLElement;
    const answerContent = item.querySelector('.faq-answer-content') as HTMLElement;
    const icon = item.querySelector('.faq-icon') as HTMLElement;

    if (!question || !answer || !answerContent) return;

    const existingTl = this.timelines.get(_index);
    if (existingTl) {
      existingTl.kill();
    }

    question.setAttribute('aria-expanded', 'true');
    item.classList.add('active');

    gsap.set(answerContent, { opacity: 1, visibility: 'visible' });
    gsap.set(answer, { height: 'auto', opacity: 1 });
    const height = answer.offsetHeight;
    gsap.set(answer, { height: 0 });
    gsap.set(answerContent, { opacity: 0 });

    const tl = gsap.timeline();

    tl.to(answer, {
      height: height,
      duration: 0.6,
      ease: 'power3.inOut',
    }, 0);

    tl.to(answerContent, {
      opacity: 1,
      y: 0,
      visibility: 'visible',
      duration: 0.5,
      ease: 'power2.out',
    }, 0.2);

    if (icon) {
      gsap.set(icon, { transformOrigin: 'center center' });
      tl.to(icon, {
        rotation: 180,
        duration: 0.5,
        ease: 'back.out(1.4)',
      }, 0);
      tl.to(icon, {
        scale: 1.15,
        duration: 0.15,
        ease: 'power2.out',
      }, 0);
      tl.to(icon, {
        scale: 1,
        duration: 0.25,
        ease: 'back.out(1.2)',
      }, 0.2);
    }

    tl.to(question, {
      scale: 1.02,
      duration: 0.3,
      ease: 'power2.out',
    }, 0);

    tl.to(item, {
      y: -8,
      boxShadow: '0 20px 60px rgba(46, 125, 110, 0.15)',
      duration: 0.4,
      ease: 'power2.out',
    }, 0);

    this.timelines.set(_index, tl);
    this.openIndex = _index;
  }

  private closeItem(_index: number): void {
    const item = this.faqItems[_index];
    if (!item) return;

    const question = item.querySelector('.faq-question') as HTMLElement;
    const answer = item.querySelector('.faq-answer') as HTMLElement;
    const answerContent = item.querySelector('.faq-answer-content') as HTMLElement;
    const icon = item.querySelector('.faq-icon') as HTMLElement;

    if (!question || !answer || !answerContent) return;

    const existingTl = this.timelines.get(_index);
    if (existingTl) {
      existingTl.kill();
    }

    question.setAttribute('aria-expanded', 'false');
    item.classList.remove('active');

    const tl = gsap.timeline();

    tl.to(answerContent, {
      opacity: 0,
      y: -10,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => {
        if (answerContent) {
          gsap.set(answerContent, {
            opacity: 0,
            visibility: 'hidden',
          });
        }
      },
    }, 0);

    tl.to(answer, {
      height: 0,
      duration: 0.5,
      ease: 'power3.inOut',
    }, 0.1);

    if (icon) {
      gsap.set(icon, { transformOrigin: 'center center' });
      tl.to(icon, {
        scale: 0.92,
        duration: 0.1,
        ease: 'power2.in',
      }, 0);
      tl.to(icon, {
        rotation: 0,
        scale: 1,
        duration: 0.35,
        ease: 'back.out(1.3)',
      }, 0.08);
    }

    tl.to(question, {
      scale: 1,
      duration: 0.3,
      ease: 'power2.in',
    }, 0);

    tl.to(item, {
      y: 0,
      boxShadow: '0 4px 24px rgba(46, 125, 110, 0.08)',
      duration: 0.4,
      ease: 'power2.in',
    }, 0);

    this.timelines.set(_index, tl);

    if (this.openIndex === _index) {
      this.openIndex = null;
    }
  }

  public dispose(): void {
    // Kill all timelines
    this.timelines.forEach((tl) => {
      tl.kill();
    });
    this.timelines.clear();

    ScrollTrigger.getAll().forEach((st) => {
      if (
        this.section &&
        (st.trigger === this.section ||
          (st.trigger && this.section.contains(st.trigger as Node)))
      ) {
        st.kill();
      }
    });
  }
}
